import React, { useContext, useEffect, useMemo, useState } from 'react';
import loadable from '@loadable/component';
import { useLocation } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import Container from 'ls-common-client/dist/components/Container';
import EmptyButton from 'ls-common-client/dist/components/EmptyButton';
import Icon from 'ls-common-client/dist/components/Icon';
import LogoutModal from './LogoutModal';
import SysMenus from './SysMenus';
import { MODALS } from '../../../constants';
import { Context } from '../../../context/AppContext';
import useDataLayerPush from '../../../hooks/useDataLayerPush';
import useFavourites from '../../../hooks/useFavourites';

const SaveButton = loadable(() => import('../../SaveButton'));

const StyledSearchIcon = styled(EmptyButton)`
  background: #ffffff;
  border-radius: 100%;
  width: 34px;
  height: 34px;
  margin-left: 5px;
  margin-right: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  i {
    color: ${props => props.theme.primary.normal};
    height: 30px;
  }
`;

const growAnimation = keyframes`
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(0.9);
  }
  80% {
    transform: scale(0.6);
  }
  100% {
    transform: scale(1);
  }
`;

const shrinkAnimation = keyframes`
  0% {
    transform: scale(1);
  }
  70% {
    transform: scale(0.1);
  }
  90% {
    transform: scale(0.3);
  }
  100% {
    transform: scale(0);
  }
`;

const StyledHeartIcon = styled(Container)`
  ${props =>
    props.grow
      ? css`
          animation-name: ${growAnimation};
        `
      : ''}
  ${props =>
    props.shrink
      ? css`
          animation-name: ${shrinkAnimation};
        `
      : ''}
  animation-duration: 0.5s;
  animation-timing-function: linear;
  height: 21px;
  transform: ${props => (props.isFavourite ? 'scale(1)' : 'scale(0)')};
`;

const LoginMenu = ({ profile, searchIcon, is404 = false }) => {
  const {
    user: { user },
    modal: { showModal },
    media: { desktop },
  } = useContext(Context);

  const favourites = useFavourites();
  const { id: userId } = user || {};

  const { pathname } = useLocation();
  const isProfile = useMemo(() => pathname.includes('/profile'), [pathname]);

  const { dataLayerPush } = useDataLayerPush();

  const [dataLayer, setDataLayer] = useState([]);

  const onDataLayerPush = menuName => {
    if (user) {
      dataLayerPush({
        event: 'User Menu Selection',
        menu_name: menuName,
        submenu_name: '',
        cta_position_on_page: 'Header Slide Menu',
      });
    } else {
      setDataLayer(prev => [...prev, { menuName }]);
    }
  };

  useEffect(() => {
    if (user && dataLayer.length > 0) {
      // Push pending menu selections to the data layer
      dataLayer.forEach(({ menuName }) => {
        onDataLayerPush(menuName);
      });
      setDataLayer([]);
    }
  }, [user, dataLayer]);

  return (
    <>
      {!!searchIcon && (
        <StyledSearchIcon
          onClick={() =>
            showModal(MODALS.SEARCH_AUTOSUGGEST, {
              inputFocus: 'keyword',
            })
          }
        >
          <Icon className="ls-icon icon-generalsearchlarge" />
        </StyledSearchIcon>
      )}
      {isProfile && !desktop && !is404 && (
        <SaveButton favourites={favourites} profile={profile} userId={userId}>
          {({ onClick, isFavourite, grow, shrink }) => {
            return (
              <EmptyButton
                onClick={onClick}
                marginRight="10px"
                height="34px"
                position="relative"
                display="flex"
                alignItems="flex-end"
              >
                <Icon
                  className="ls-icon icon-heartcutout"
                  color="white"
                  fontSize="34px"
                />
                <Container
                  position="absolute"
                  left="50%"
                  top="50%"
                  transform="translate(-50%,-50%)"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <StyledHeartIcon
                    isFavourite={isFavourite}
                    grow={grow}
                    shrink={shrink}
                  >
                    <Icon
                      fontSize="18px"
                      iconColor="#f16159"
                      className="ls-icon icon-heartsolid"
                      marginTop="2px"
                    />
                  </StyledHeartIcon>
                </Container>
              </EmptyButton>
            );
          }}
        </SaveButton>
      )}
      <SysMenus />
      <LogoutModal />
    </>
  );
};

export default LoginMenu;
